const normalizeBase = (base?: string): string => {
  if (!base) return '/api';
  let b = base.trim();
  // Entferne trailing Slashes
  b = b.replace(/\/+$/,'');
  // Wenn keinerlei /api Segment vorhanden ist, hinten anfuegen
  if (!/\/api(\/|$)/i.test(b)) b = `${b}/api`;
  return b;
};

const DEFAULT_BASE_URL = normalizeBase(process.env.REACT_APP_API_BASE_URL || '');

export class ApiClient {
  constructor(private readonly baseUrl: string = DEFAULT_BASE_URL) {
    this.baseUrl = normalizeBase(baseUrl);
  }

  private buildUrl(path: string): string {
    if (/^https?:/i.test(path)) return path;
    const base = (this.baseUrl || '').replace(/\/+$/,'');
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${base}${p}`;
  }

  private async handleResponse<T>(method: string, url: string, res: Response): Promise<T> {
    if (res.ok) {
      // Try JSON, fallback to empty object
      try { return await res.json() as T; } catch { return {} as T; }
    }
    let bodySnippet = '';
    try {
      const text = await res.text();
      if (text) {
        // Versuche JSON zu parsen um Validierungsdetails anzureichern
        let enriched = text;
        try {
          const json = JSON.parse(text);
          // Erwarte mögliche Strukturen von Spring Validation / generischen Fehlerobjekten
          const parts: string[] = [];
          const collect = (arr: any[]) => {
            for (const e of arr) {
              const field = e.field || e.objectName || e.path || e.parameter || e.name;
              const msg = e.message || e.defaultMessage || e.error || e.code;
              if (field || msg) parts.push(`${field ? field + ': ' : ''}${msg || ''}`.trim());
            }
          };
          if (Array.isArray((json as any).errors)) collect((json as any).errors);
          if (Array.isArray((json as any).fieldErrors)) collect((json as any).fieldErrors);
          if (Array.isArray((json as any).violations)) collect((json as any).violations);
          if (parts.length) enriched += `\nValidation: ${parts.slice(0, 15).join('; ')}`;
        } catch {
          // ignore parse error
        }
        bodySnippet = ` Body: ${enriched.slice(0,500)}`; // etwas mehr Spielraum für Validierung
      }
    } catch {}
    throw new Error(`${method} ${url} failed: ${res.status} ${res.statusText}.${bodySnippet}`);
  }

  async get<T>(path: string): Promise<T> {
    const url = this.buildUrl(path);
    let res: Response;
    try {
      res = await fetch(url, { credentials: 'include' });
    } catch (e) {
      throw new Error(`Network GET ${url} failed: ${(e as Error).message}`);
    }
    return this.handleResponse<T>('GET', url, res);
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    const url = this.buildUrl(path);
    let res: Response;
    try {
      if (process.env.NODE_ENV !== 'production') {
        // Debug-Ausgabe
        console.debug('[ApiClient] POST', url, 'Payload:', body);
      }
      res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      });
    } catch (e) {
      throw new Error(`Network POST ${url} failed: ${(e as Error).message}`);
    }
    return this.handleResponse<T>('POST', url, res);
  }

  async put<T>(path: string, body: unknown): Promise<T> {
    const url = this.buildUrl(path);
    let res: Response;
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[ApiClient] PUT', url, 'Payload:', body);
      }
      res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      });
    } catch (e) {
      throw new Error(`Network PUT ${url} failed: ${(e as Error).message}`);
    }
    return this.handleResponse<T>('PUT', url, res);
  }

  async delete(path: string): Promise<void> {
    const url = this.buildUrl(path);
    let res: Response;
    try {
      res = await fetch(url, { method: 'DELETE', credentials: 'include' });
    } catch (e) {
      throw new Error(`Network DELETE ${url} failed: ${(e as Error).message}`);
    }
    if (!res.ok) {
      let bodySnippet = '';
      try {
        const text = await res.text();
        if (text) bodySnippet = ` Body: ${text.slice(0,200)}`;
      } catch {}
      throw new Error(`DELETE ${url} failed: ${res.status} ${res.statusText}.${bodySnippet}`);
    }
  }
}
